import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Reproduit le filtre Liquid `handleize` (translitère les accents, minuscule,
 * remplace tout non alphanumérique par un tiret). Sert à matcher la valeur
 * d'option du variant courant contre les `value` du <select> de saveurs
 * (générées côté Liquid via `| handleize`).
 * @param {string} value
 * @returns {string}
 */
const handleize = (value) =>
	String(value)
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '');

/**
 * @typedef {Object} AxProductFocusRefs
 * @property {HTMLDialogElement} [certifsModal]
 * @property {HTMLDetailsElement} [compositionDetails]
 * @property {HTMLElement & { swiper: any; initialize: () => void }} [ingredientsSwiper]
 */

/**
 * Custom element for the Ax Product Focus section.
 *
 * Handles:
 *   1. Certifs side-panel <dialog> open/close.
 *   2. Ingredients Swiper carousel inside the composition accordion :
 *      mobile peek (0.5 / 1 / 0.5), desktop 3 centered. Swiper measurements
 *      need to be refreshed when the accordion opens (block-size: 0 while
 *      closed → swiper-container has zero width and slidesPerView breaks).
 *   3. "Show more / less" toggle on long ingredient texts.
 *
 * Le widget d'avis REVIEWS.io s'auto-hydrate via `<reviews-io-widget>`
 * (assets/reviews-io-widget.js) — plus rien à gérer ici.
 *
 * @extends {Component<AxProductFocusRefs>}
 */
class AxProductFocus extends Component {
	/** @type {any} */
	#ingredientsSwiper = null;

	async connectedCallback() {
		document.addEventListener('click', this.#onDocumentClick);
		this.#openAnchoredDetails();
		super.connectedCallback();
		// Sync le sélecteur de saveur composition quand le variant change (option Saveur).
		document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
		if (this.refs.ingredientsSwiper) {
			await customElements.whenDefined('swiper-container');
			this.#initIngredientsSwiper();
		}
	}

	disconnectedCallback() {
		document.removeEventListener('click', this.#onDocumentClick);
		super.disconnectedCallback();
		document.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
		const modal = this.refs.certifsModal;
		if (modal?.open) modal.close();
		try {
			this.#ingredientsSwiper?.destroy(true, true);
		} catch (_) {
			// Swiper can throw during teardown if already destroyed.
		}
		this.#ingredientsSwiper = null;
	}

	openCertifsModal() {
		const modal = this.refs.certifsModal;
		if (!modal) return;
		if (typeof modal.showModal === 'function') {
			modal.showModal();
		} else {
			modal.setAttribute('open', '');
		}
	}

	closeCertifsModal() {
		const modal = this.refs.certifsModal;
		if (!modal) return;
		if (typeof modal.close === 'function') {
			modal.close();
		} else {
			modal.removeAttribute('open');
		}
	}

	handleFlavorChange(event) {
		if (!(event.target instanceof HTMLSelectElement)) return;
		this.#applyFlavor(event.target, event.target.value);
	}

	/**
	 * Affiche le contenu + les tables (nut/amino) de la saveur `value` et masque
	 * les autres. Partagé entre le changement manuel du <select> et la synchro
	 * automatique sur changement de variant.
	 * @param {HTMLSelectElement} select
	 * @param {string} value - clé de saveur (handleize)
	 */
	#applyFlavor(select, value) {
		const wrap = select.closest('.ax-product-focus__flavor');
		if (!wrap) return;

		const contents = wrap.querySelectorAll('[data-flavor-id]');
		for (const div of contents) {
			div.hidden = div.dataset.flavorId !== value;
		}

		const content = select.closest('.ax-product-focus__content');
		if (content) {
			const flavorTables = content.querySelectorAll('[data-flavor-table]');
			for (const div of flavorTables) {
				div.hidden = div.dataset.flavorTable !== value;
			}
		}
	}

	/**
	 * Aligne le sélecteur de saveur composition sur le variant courant lorsque
	 * l'utilisateur change de saveur via le variant-picker principal (pas de
	 * re-render de cette section sur changement de variant).
	 * @param {Event & { detail?: { resource?: any, data?: { productId?: string } } }} event
	 */
	#handleVariantUpdate = (event) => {
		const variant = event.detail?.resource;
		if (!variant) return;

		// Ne réagir qu'au produit de cette section (ignore les quick-add d'autres produits).
		const eventProductId = event.detail?.data?.productId;
		if (eventProductId && this.dataset.productId && String(eventProductId) !== this.dataset.productId) return;

		const select = this.querySelector('.ax-product-focus__flavor-select');
		if (!(select instanceof HTMLSelectElement)) return;

		const validKeys = new Set(Array.from(select.options, (option) => option.value));
		const optionValues = Array.isArray(variant.options)
			? variant.options
			: [variant.option1, variant.option2, variant.option3];

		for (const optionValue of optionValues) {
			if (optionValue == null) continue;
			const key = handleize(optionValue);
			if (!validKeys.has(key)) continue;
			if (select.value !== key) {
				select.value = key;
				this.#applyFlavor(select, key);
			}
			return;
		}
	};

	handleTableChange(event) {
		if (!(event.target instanceof HTMLSelectElement)) return;
		const select = event.target;
		const value = select.value;
		const flavorWrap = select.closest('[data-flavor-table]');
		const scope = flavorWrap ?? select.closest('.ax-product-focus__composition-table');
		const group = scope?.querySelector('[data-table-group]');
		if (!group) return;

		const tables = group.querySelectorAll('[data-table-id]');
		for (const table of tables) {
			table.hidden = table.dataset.tableId !== value;
		}
	}

	handleCompositionToggle(event) {
		if (!(event.target instanceof HTMLDetailsElement)) return;
		if (!event.target.open) return;
		// Swiper can't measure while details is closed (block-size: 0).
		// Refresh on open so slidesPerView / centeredSlides apply correctly.
		this.#ingredientsSwiper?.update();
	}

	toggleIngredientText(event) {
		// Framework delegates events on document — event.target is proxied to the
		// element bearing the on:click attribute (the <button>), not the inner span.
		const button = event.target;
		if (!(button instanceof HTMLButtonElement)) return;
		const slide = button.closest('.ax-product-focus__ingredient');
		const text = slide?.querySelector('.ax-product-focus__ingredient-text');
		if (!(text instanceof HTMLElement)) return;

		const collapsed = text.dataset.collapsed === 'true';
		text.dataset.collapsed = collapsed ? 'false' : 'true';
		button.setAttribute('aria-expanded', collapsed ? 'true' : 'false');

		const more = button.querySelector('.ax-product-focus__ingredient-toggle-more');
		const less = button.querySelector('.ax-product-focus__ingredient-toggle-less');
		if (more instanceof HTMLElement) more.hidden = collapsed;
		if (less instanceof HTMLElement) less.hidden = !collapsed;

		this.#ingredientsSwiper?.update();
	}

	/**
	 * Le lien « X avis » (réglage `count_url` du bloc note, dans la buy-box) pointe sur
	 * l'accordéon : l'ancre native défile mais n'ouvre pas un `<details>` fermé. On ouvre
	 * ici. Et si la cible n'existe pas — le client a saisi une ancre en dur qui embarque
	 * un id de template périmé — on route vers l'accordéon avis de cette section.
	 */
	#onDocumentClick = (event) => {
		const lien = event.target.closest('a[href^="#"]');
		if (!lien) return;

		let cible = null;
		try {
			cible = document.querySelector(lien.getAttribute('href'));
		} catch {
			/* href invalide comme sélecteur : traité comme cible absente */
		}

		if (!cible && lien.classList.contains('product-rating__count')) {
			const repli = this.querySelector('.ax-product-focus__details--reviews');
			if (!repli) return;
			event.preventDefault();
			repli.open = true;
			repli.scrollIntoView({ behavior: 'smooth', block: 'start' });
			return;
		}

		if (lien.hasAttribute('data-find-out-more') && (!cible || cible.contains(this))) {
			const description = this.querySelector('.ax-product-focus__details--description');
			if (description) {
				event.preventDefault();
				description.open = true;
				description.scrollIntoView({ behavior: 'smooth', block: 'start' });
				return;
			}
		}

		// Cible valide : on ouvre les <details> concernés et on laisse l'ancre native défiler.
		const details = cible?.closest('details') ?? (cible?.matches?.('details') ? cible : null);
		if (details && this.contains(details)) details.open = true;
	};

	/** Arrivée par URL ancrée (lien partagé) : même ouverture qu'au clic. */
	#openAnchoredDetails() {
		if (!location.hash) return;
		let cible = null;
		try {
			cible = document.querySelector(location.hash);
		} catch {
			return;
		}
		const details = cible?.closest('details');
		if (details && this.contains(details)) details.open = true;
	}

	#initIngredientsSwiper() {
		const swiperEl = this.refs.ingredientsSwiper;
		if (!swiperEl || swiperEl.swiper) return;

		const slideCount = swiperEl.querySelectorAll('swiper-slide').length;

		// Le centrage n'a de sens que s'il y a de quoi faire défiler : sous le nombre
		// d'emplacements visibles (2 en mobile, 3 en desktop), il laissait un trou à
		// gauche et poussait même la 1re tuile hors écran (ticket 12744182519).
		const isCarouselMobile = slideCount > 2;
		const isCarouselDesktop = slideCount > 3;

		Object.assign(swiperEl, {
			// Ancrage à gauche : pas de centrage, la tuile sélectionnée vient se caler au
			// bord gauche à chaque clic, et l'arrivée montre la première tuile ferrée à
			// gauche (ticket 12744182519, 2e retour). Les deux dernières tuiles ne peuvent
			// pas atteindre le bord (fin de course) : leur sélection reste possible parce
			// qu'elle est portée par la classe `is-selected`, pas par l'état Swiper.
			slidesPerView: 2,
			spaceBetween: 12,
			initialSlide: 0,
			grabCursor: true,
			a11y: true,
			keyboard: { enabled: true },
			watchSlidesProgress: true,
			observer: true,
			observeParents: true,
			pagination: { clickable: true },
			breakpoints: {
				750: {
					// Desktop: 3 slides, no offscreen peek
					slidesPerView: 3,
					spaceBetween: 24,
				},
			},
		});

		swiperEl.initialize();
		swiperEl.removeAttribute('init');

		this.#ingredientsSwiper = swiperEl.swiper;

		// Le clic sélectionne la tuile au lieu de quitter la fiche produit
		// (le lien vers la page ingrédient a été retiré, ticket 12744182519).
		// Sans carrousel il n'y a rien à sélectionner : toutes les tuiles sont déjà
		// lisibles. Le bouton « Lire la suite » garde son propre comportement.
		if (isCarouselMobile || isCarouselDesktop) {
			// La sélection (bordure + pleine opacité) est une CLASSE posée ici, pas l'état
			// `swiper-slide-active` : avec le centrage borné, les tuiles de bord ne peuvent
			// jamais être « actives » au sens Swiper — accrocher le style à l'état natif
			// laissait la première et la dernière estompées sans recours (2e retour du
			// ticket 12744182519). Le carrousel ne gère plus que le positionnement.
			const selectionner = (slide) => {
				for (const s of swiperEl.querySelectorAll('.ax-product-focus__ingredient')) {
					s.classList.toggle('is-selected', s === slide);
				}
			};
			selectionner(swiperEl.querySelector('.ax-product-focus__ingredient'));

			swiperEl.addEventListener('click', (event) => {
				const slide = event.target.closest('.ax-product-focus__ingredient');
				if (!slide) return;
				selectionner(slide);

				// « Lire la suite » sélectionne la tuile (on ne lit pas un texte estompé)
				// mais ne déclenche pas de recentrage : le dépliage suffit comme mouvement.
				if (event.target.closest('.ax-product-focus__ingredient-toggle')) return;

				const index = [...swiperEl.querySelectorAll('swiper-slide')].indexOf(slide);
				if (index >= 0) this.#ingredientsSwiper?.slideTo(index);
			});
		}

		this.#updateIngredientToggles();
	}

	#updateIngredientToggles() {
		const slides = this.querySelectorAll('.ax-product-focus__ingredient');
		for (const slide of slides) {
			const text = slide.querySelector('.ax-product-focus__ingredient-text');
			const toggle = slide.querySelector('.ax-product-focus__ingredient-toggle');
			if (!(text instanceof HTMLElement) || !(toggle instanceof HTMLElement)) continue;
			// Hide toggle if text doesn't overflow its clamped container
			toggle.hidden = text.scrollHeight <= text.clientHeight;
		}
	}
}

if (!customElements.get('ax-product-focus')) {
	customElements.define('ax-product-focus', AxProductFocus);
}
